import isEqual from 'lodash.isequal';
import {
  IAnotherPerson,
  ICartItem,
  INightTimeAddedTerms,
  IPreOrderData,
  IProviderItem,
  MaibCardItem,
  SendNewOrderParams,
  VoucherType,
} from 'types';
import {v4 as uuidv4} from 'uuid';

import {isMobile} from './common';
import logger from './logger';
import {parseUTM} from './strings';

/**
 * Calculate night surcharge
 *
 * @param nighttime_added_terms
 * added_price - 10
 * range - [0, 15]
 * @param preorder
 * hour - 18:00
 * @returns
 */
export const calculateNightPrice = (nighttime_added_terms?: INightTimeAddedTerms, preorder?: IPreOrderData) => {
  let defaultPrice = 0;
  if (!nighttime_added_terms) return defaultPrice;
  const hoursRange = nighttime_added_terms.hours_range;

  const hasRange = hoursRange && hoursRange.length == 2;
  if (!hasRange) return defaultPrice;

  const currentDate = new Date();
  const preorderDate = preorder && preorder.hour.value.split(':');

  //  Chisinau Timezone
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
    timeZone: 'Europe/Chisinau', // Set the timezone to Chisinau
  };
  const currentDateTimeString = currentDate.toLocaleTimeString('en-US', options);
  const [chisinauHours, chisinauMinutes] = currentDateTimeString.split(':').map((str) => parseInt(str));

  const currentHour = preorderDate ? Number(preorderDate[0]) : chisinauHours;
  const currentMinutes = preorderDate ? Number(preorderDate[1]) : chisinauMinutes;

  logger.logDebug(`currentHour: ${currentHour} - ${currentMinutes} | ${hoursRange}`);

  const secondDateValidation = currentHour < hoursRange[1] || (currentHour == hoursRange[1] && currentMinutes == 0);

  if (currentHour >= hoursRange[0] && secondDateValidation) {
    defaultPrice = nighttime_added_terms.added_price;
  }

  return defaultPrice;
};

/**
 *
 * @param cartProduct
 * @param product
 * @returns
 */
export function isSameProduct(cartProduct: ICartItem, product: ICartItem) {
  return (
    cartProduct.productId === product.productId &&
    cartProduct.instructions === product.instructions &&
    isEqual(cartProduct.selectedOptions, product.selectedOptions)
  );
}

export const isProviderUnavalaible = (provider: IProviderItem) => {
  return provider?.is_on_pause || (!provider?.is_open && !provider?.accepts_preorders);
};

/**
 * Generate params for new Order [card | cash]
 */
export const createNewOrderParams = (
  formikValues: SendNewOrderParams,
  cartTimestamp: Date,
  preOrder: IPreOrderData | undefined,
  voucher: VoucherType | undefined,
  another_person: IAnotherPerson,
  payment_for_self: boolean,
  isOnlinePayment: boolean,
  selectedCard: MaibCardItem | undefined,
  saveCard: boolean,
) => {
  const ismobile = isMobile();
  const orderParams: SendNewOrderParams = {
    ...formikValues,
    app: ismobile ? 'website_mobile' : 'website_pc',
    cart_uuid: uuidv4(),
    cart_created: cartTimestamp,
    contactless_delivery: false,
    order_for_another: false,
    promo_conditions_agree: true,
    payment_for_self,
  };

  // card payment (maybe will remove in future | need to check on be)
  if (isOnlinePayment) {
    const {customer} = formikValues;
    orderParams.payer = {
      payment_for_self: customer.name,
      name: customer.name,
      phone: customer.phonenumber,
      email: customer.email,
    };
    if (selectedCard?.id) orderParams.saved_card_id = selectedCard.id;
    if (saveCard) orderParams.save_card = saveCard;
  }

  if (preOrder) orderParams.preorder = {day: preOrder.value, hour: preOrder.hour.value};

  if (voucher?.promoCode) orderParams.promo_code = voucher.promoCode;

  if (!payment_for_self && another_person?.name.length) {
    orderParams.another_person = another_person;
    orderParams.order_for_another = !!another_person;
  }
  const regionName = window?.localStorage?.getItem('region_name');
  if (regionName) {
    orderParams.region_name = regionName;
    window?.localStorage?.removeItem('region_name');
  }

  /**
   * Tracking params
   * Utm + Referrer
   */
  const refferer = localStorage.getItem('referrer');
  if (refferer) {
    orderParams.refferer = refferer;
    localStorage.removeItem('referrer');
  }

  const storedUrl = localStorage.getItem('utm_url_source');
  if (storedUrl) {
    const params = parseUTM(storedUrl);
    orderParams.utm_data = params;

    orderParams.link = storedUrl;
    localStorage.removeItem('utm_url_source');
  }
  return orderParams;
};

// Todo: move to ts all this shit and refactor to normal
export const calculateProductOptionsPrice = (item: ICartItem, calculateQty = false) => {
  let optionsPrice = 0;
  let discountOptions = 0;
  if (!item?.selectedOptions) return {optionsPrice, discountOptions};

  Object.keys(item.selectedOptions).map((setId) => {
    Object.values((item.selectedOptions ?? {})[parseInt(setId)]).map((option_set) => {
      optionsPrice += option_set.price * option_set.quantity * (calculateQty ? item.quantity : 1);
      discountOptions += option_set.discount_price * (calculateQty ? item.quantity : 1);
    });
  });

  return {optionsPrice, discountOptions};
};

export const validateCardType = (cardNumber: string | undefined): 'VISA' | 'MASTERCARD' | 'UNKNOWN' => {
  if (!cardNumber) return 'UNKNOWN';
  const visaRegex = /^4[0-9]{12}(?:[0-9]{3})?$/;
  const masterCardRegex = /^5[1-5][0-9]{14}$/;

  if (visaRegex.test(cardNumber)) return 'VISA';
  if (masterCardRegex.test(cardNumber)) return 'MASTERCARD';

  return 'UNKNOWN';
};
